import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Instagram, Music2, Linkedin, Film, Mail, Lock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PLATFORMS = [
    { key: "instagram", label: "Instagram", icon: Instagram, plans: ["free", "starter", "pro"] },
    { key: "tiktok", label: "TikTok", icon: Music2, plans: ["free", "starter", "pro"] },
    { key: "linkedin", label: "LinkedIn Thread", icon: Linkedin, plans: ["pro"] },
    { key: "shorts", label: "Shorts / Reels", icon: Film, plans: ["pro"] },
    { key: "newsletter", label: "Newsletter", icon: Mail, plans: ["pro"] },
];

export default function NewRepurpose() {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [mode, setMode] = useState("text");
    const [text, setText] = useState("");
    const [yt, setYt] = useState("");
    const [selected, setSelected] = useState(["instagram", "tiktok"]);
    const [loading, setLoading] = useState(false);

    const toggle = (key) => {
        if (!user) return;
        if (!PLATFORMS.find((p) => p.key === key).plans.includes(user.plan)) {
            toast.error("This platform requires Pro plan");
            return;
        }
        setSelected((prev) => prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]);
    };

    const submit = async () => {
        if (selected.length === 0) {
            toast.error("Pick at least one platform");
            return;
        }
        if (mode === "text" && text.trim().length < 30) {
            toast.error("Paste at least 30 characters of text");
            return;
        }
        if (mode === "youtube" && !yt.match(/youtu/i)) {
            toast.error("Enter a valid YouTube URL");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/repurpose", {
                source_type: mode,
                source: mode === "text" ? text : yt,
                platforms: selected,
            });
            await refresh();
            toast.success("Generated!");
            nav(`/app/result/${data.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Generation failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 sm:p-10 max-w-5xl mx-auto" data-testid="new-root">
            <div className="font-mono-ui text-xs tracking-[0.25em] uppercase text-zinc-500 mb-2">// NEW REPURPOSE</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Paste it. We split it.</h1>
            <p className="mt-3 text-zinc-400 max-w-2xl">A YouTube link or some text. We turn it into posts, threads and scripts.</p>

            <div className="mt-10 border border-white/10 p-6 sm:p-8 bg-[#0a0a0a]">
                <Tabs value={mode} onValueChange={setMode}>
                    <TabsList className="bg-transparent border border-white/10 rounded-none p-0 h-auto" data-testid="new-tabs">
                        <TabsTrigger value="text" data-testid="tab-text" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black font-mono-ui text-xs tracking-widest uppercase px-5 py-2.5">
                            Paste text
                        </TabsTrigger>
                        <TabsTrigger value="youtube" data-testid="tab-youtube" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black font-mono-ui text-xs tracking-widest uppercase px-5 py-2.5">
                            YouTube link
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="text" className="mt-6">
                        <Textarea
                            data-testid="new-text-input"
                            placeholder="Paste your blog post, video transcript, podcast notes, or raw idea here…"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={10}
                            className="bg-transparent border-white/15 focus:border-white text-white resize-none font-mono-ui text-sm"
                        />
                        <div className="mt-2 font-mono-ui text-[10px] tracking-widest uppercase text-zinc-600">{text.length} chars</div>
                    </TabsContent>

                    <TabsContent value="youtube" className="mt-6">
                        <Input
                            data-testid="new-yt-input"
                            placeholder="https://youtube.com/watch?v=..."
                            value={yt}
                            onChange={(e) => setYt(e.target.value)}
                            className="bg-transparent border-white/15 focus:border-white text-white h-12 font-mono-ui text-sm"
                        />
                        <div className="mt-2 font-mono-ui text-[10px] tracking-widest uppercase text-zinc-600">
                            We&apos;ll extract title &amp; channel from the video.
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Platforms */}
            <div className="mt-10">
                <div className="font-mono-ui text-xs tracking-[0.25em] uppercase text-zinc-500 mb-4">// PLATFORMS</div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border border-white/10">
                    {PLATFORMS.map((p) => {
                        const locked = user && !p.plans.includes(user.plan);
                        const active = selected.includes(p.key);
                        return (
                            <button
                                key={p.key}
                                data-testid={`platform-${p.key}`}
                                onClick={() => toggle(p.key)}
                                disabled={locked}
                                className={`relative p-5 text-left transition-colors ${
                                    active ? "bg-white text-black" : "bg-[#0a0a0a] text-white hover:bg-white/5"
                                } ${locked ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                                <p.icon className="w-5 h-5 mb-3" strokeWidth={1.5} />
                                <div className="font-display font-bold text-sm">{p.label}</div>
                                {locked && (
                                    <div className="mt-2 flex items-center gap-1 font-mono-ui text-[9px] tracking-widest uppercase text-zinc-500">
                                        <Lock className="w-3 h-3" strokeWidth={1.5} /> Pro only
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                <div className="font-mono-ui text-xs uppercase tracking-widest text-zinc-500" data-testid="new-credit-info">
                    {user?.monthly_limit - user?.credits_used} repurposes left on {user?.plan}
                </div>
                <Button
                    data-testid="new-generate-btn"
                    size="lg"
                    onClick={submit}
                    disabled={loading}
                    className="bg-white text-black hover:bg-zinc-200 font-semibold h-12 px-8"
                >
                    {loading ? "Generating…" : <><Sparkles className="w-4 h-4 mr-2" strokeWidth={1.8} /> Generate ({selected.length})</>}
                </Button>
            </div>
        </div>
    );
}
